import {
  DateTime, Duration, DurationObject, DateObject
} from 'luxon';
import { validate } from 'validate.js';
import cloneDeep from 'lodash/cloneDeep';
import { TODAY } from 'constants/dateLimits';
import { DateModel, DateRangeModel } from 'forms/shared/types';
import { EmptyDate } from 'forms/shared/validators/models/date/instance';
import { SF852013 } from 'constants/formTypes';
import { DeepSafePartial } from 'types/types';
import { SafeDateTime, ValidSafeDateTime } from './types';
import { SafeDateTimeRange, validateDateRanges } from './dates/dateRange';

export const today = TODAY;

/**
 * @deprecated - Use getSafeDateTime instead
 */
export const isDateTime = (obj = {}) => DateTime.isDateTime(obj);

/**
 * @deprecated - Use getSafeDateTime instead
 */
export const cleanDateObject = (obj) => {
  // Return param if it's not a plain object (it will be invalid)
  if (!validate.isHash(obj)) { return obj; }

  // Allowed object keys (per luxon spec)
  const {
    year,
    month,
    day,
    ordinal,
    weekYear,
    weekNumber,
    weekday,
    hour,
    minute,
    second,
    millisecond,
    zone,
    locale,
    outputCalendar,
    numberingSystem,
  } = obj;

  return {
    year,
    month,
    day,
    ordinal,
    weekYear,
    weekNumber,
    weekday,
    hour,
    minute,
    second,
    millisecond,
    zone,
    locale,
    outputCalendar,
    numberingSystem,
  };
};

/**
 * parseDateFromDateModel will be a replacement for createDateFromObject.
 * CreateDateFromObject is not type-safe, is not explicit, and returns an error
 * that doesn't match.
 *
 * DateObject is a close match to DateModel.
 */
export const parseDateFromDateModel = (
  dateModel: Partial<DateModel> | Partial<DateObject>
): SafeDateTime => {
  const {
    day: argDay, month: argMonth, year: argYear, minute, second, hour, millisecond
  } = {
    ...EmptyDate(),
    ...dateModel,
  };
  const day = typeof argDay === 'number' ? argDay : parseInt(argDay, 10);
  const month = typeof argMonth === 'number' ? argMonth : parseInt(argMonth, 10);
  const year = typeof argYear === 'number' ? argYear : parseInt(argYear, 10);
  const valid = !Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year);

  if (valid === false) {
    return {
      valid: false,
      datetime: undefined,
    };
  }
  const datetime = DateTime.fromObject({
    day, month, year, minute, second, hour, millisecond
  });

  return {
    valid: datetime.isValid,
    datetime,
  };
};

/**
 * SafeDateTimeInputTypes are all the possible types that can be converted into
 * a SafeDateTime object.
 */
type SafeDateTimeInputTypes = DeepSafePartial<DateModel> | DateTime | DateObject | undefined | null

/**
 * This is a helper function to handle multiple types and result in a safe Luxon
 * DateTime object.
 *
 * This can completely replace createDateFromObject & cleanDateObject combo.
 */
export const getSafeDateTime = (date: SafeDateTimeInputTypes): SafeDateTime => {
  if (date === undefined || date === null) {
    return {
      valid: false,
      datetime: undefined,
    };
  }

  if (DateTime.isDateTime(date)) {
    return {
      valid: date.isValid,
      datetime: date,
    };
  }
  return parseDateFromDateModel(date);
};

export const isValidSafeDateTime = (date: SafeDateTime): date is ValidSafeDateTime => date.valid === true;

/**
 * @deprecated - Use getSafeDateTime instead
 */
export const createDateFromObject = (data) => {
  try {
    return DateTime.fromObject(data);
  } catch (e) {
    return NaN;
  }
};

/**
 * @deprecated - Use getSafeDateTime instead
 */
export const createDateFromTimestamp = (ts) => {
  try {
    return DateTime.fromMillis(ts);
  } catch (e) {
    return NaN;
  }
};

export const createDurationFromObject = (data: DurationObject) => {
  try {
    return Duration.fromObject(data);
  } catch (e) {
    return NaN;
  }
};

export const dateWithinRange = (
  date: DeepSafePartial<DateModel> | DateObject | undefined,
  range: DurationObject
): boolean => {
  const duration = Duration.fromObject(range);
  const boundary = today.minus(duration);

  return boundary <= createDateFromObject(cleanDateObject(date));
};

/**
 * This is a helper function to handle knowing if a date within a month or sooner.
 * This supports edge case of being in the same month by comparing the edge month
 * with out the date.
 *
 */
export const dateWithinMonthYearSafeRange = (
  date: SafeDateTimeInputTypes,
  backDate: SafeDateTimeInputTypes
): boolean => {
  const safeDate = getSafeDateTime(date);
  const safeBackDate = getSafeDateTime(backDate);
  if (!safeDate.valid || !safeBackDate.valid) { return false; }
  return (
    (safeDate.datetime.hasSame(safeBackDate.datetime, 'month')
      && safeDate.datetime.hasSame(safeBackDate.datetime, 'year'))
    || safeDate.datetime >= safeBackDate.datetime
  );
};

/**
 * Helper function that returns true if two valid safeDateTime objects are
 * provided and the first occurs after the second
 *
 * @param dateTimeFuture
 * @param dateTimePast
 * @returns boolean
 */
export const isSafeDateLater = (
  dateTimeFuture: SafeDateTime,
  dateTimePast: SafeDateTime,
): boolean => (dateTimeFuture && dateTimePast && dateTimeFuture.valid && dateTimePast.valid && dateTimePast.datetime < dateTimeFuture.datetime);

/**
 * @param {Object} date
 * @param {Object} range
 * @returns {boolean} boolean
 */
export const dateNoDayWithinRange = (date, range) => {
  const duration = Duration.fromObject(range);
  const newToday = today.set({ day: 1 });
  const boundary = newToday.minus(duration);

  return boundary <= createDateFromObject(cleanDateObject(date));
};

export const sortDateObjects = (dates: DateModel[] = []) => {
  const sortFn = (a, b) => {
    if (a < b) { return -1; }
    if (a > b) { return 1; }
    return 0;
  };

  return dates
    .filter((d) => !!d)
    .map((d) => createDateFromObject(cleanDateObject(d)))
    .sort(sortFn);
};

/**
 * Converts all possible date objects (including undefined/null) and returns
 * only valid DateTimes as a ValidSafeDateTime.
 */
export const getValidatedDates = (dates: SafeDateTimeInputTypes[] = []): ValidSafeDateTime[] =>
  // @ts-ignore
  dates.map((d) => getSafeDateTime(d)).filter((d) => d.valid === true);// eslint-disable-line implicit-arrow-linebreak

/**
 * Sorts SafeDateTimes as a new object.
 */
export const sortSafeDateTimes = <t extends SafeDateTime>(dates: t[] = []): t[] => [...dates].sort((a, b) => {
  if (a.datetime === undefined || b.datetime === undefined) { return 0; }

  if (a.datetime < b.datetime) { return -1; }
  if (a.datetime > b.datetime) { return 1; }
  return 0;
});

// get applicant age (today - birthdate) in years
// if # of years - 18 >= required years, return required years
// if # of years - 18 < required years, > 2, return difference
// return 2
export const getApplicantRequiredDuration = ({
  requiredDuration = { years: 2 }, applicantBirthdate, thresholdYears = 18, formType, type = '', minimumYears = undefined
}: { requiredDuration: any; applicantBirthdate?: DateModel, thresholdYears?: number, formType?: string, type?: string, minimumYears?: number }) => {
  let minYears = formType === SF852013 && ['education', 'employment'].includes(type) ? 0 : 2;
  if (minimumYears !== undefined) {
    minYears = minimumYears;
  }
  const birthdate = formType === SF852013 && type === 'residence' ? null : applicantBirthdate;
  const duration = Duration.fromObject(requiredDuration);
  if (!duration.isValid) {
    return {
      requiredYears: minYears,
      coverage: { years: minYears }
    };
  }

  const durationYears = duration.as('years');
  if (!birthdate) {
    return {
      requiredYears: durationYears,
      coverage: { years: durationYears }
    };
  }

  const birthdateDate = createDateFromObject(cleanDateObject(birthdate));
  if (!birthdateDate.isValid) {
    return {
      requiredYears: durationYears,
      coverage: { years: durationYears }
    };
  }

  const thresholdDate = getSafeDateTime(birthdateDate).datetime?.plus({ years: thresholdYears });

  const applicableYears = today.diff(thresholdDate).as('years');
  const applicableDays = today.diff(thresholdDate).as('days');

  if (applicableYears >= durationYears) {
    return {
      requiredYears: durationYears,
      coverage: { years: durationYears }
    };
  }
  if (applicableYears >= minYears) {
    return {
      requiredYears: Math.round(applicableYears),
      coverage: { days: applicableDays }
    };
  }
  return {
    requiredYears: minYears,
    coverage: { years: minYears }
  };
};

/** Convert date objects to luxon objects and sort by from date */
export const sortDateRanges = <t extends SafeDateTimeRange>(ranges: t[]): t[] => [...ranges].sort((a, b) => {
  if (a.from.datetime === undefined || b.from.datetime === undefined) { return 0; }

  if (a.from.datetime < b.from.datetime) { return -1; }
  if (a.from.datetime > b.from.datetime) { return 1; }
  return 0;
});

/** Determine whether two daterange objects have overlap */
export function daterangeOverlap(firstDaterange: SafeDateTimeRange, secondDaterange: SafeDateTimeRange) {
  if (firstDaterange.valid && secondDaterange.valid
    && (firstDaterange.from.datetime <= secondDaterange.to.datetime && firstDaterange.to.datetime >= secondDaterange.from.datetime)
    || (secondDaterange.from.datetime <= firstDaterange.to.datetime && secondDaterange.to.datetime >= firstDaterange.from.datetime)) {
    return true;
  }

  return false;
}

/**
 * All dates being operated on are luxon date objects
 */

export const withinOneMonth = (start: DateTime, end: DateTime) => end?.diff(start, 'months').as('months') <= 1;

const shouldUpdateRightBoundry = ({
  from, to, requiredRange, rightBoundary
}) => {
  // coverage overlap
  const hasCoverageOverlap = from?.datetime <= rightBoundary?.datetime && to?.datetime > rightBoundary?.datetime;

  // 'from' date is within one month of previous 'to' date; set new boundry
  const withinOneMonthOfPrev = from?.datetime >= rightBoundary?.datetime
    && to?.datetime >= rightBoundary?.datetime
    && to?.datetime <= requiredRange?.to
    && withinOneMonth(rightBoundary?.datetime, from?.datetime);

  return hasCoverageOverlap || withinOneMonthOfPrev;
};

export const getRequiredRange = (coverage) => {
  const coverageDuration = Duration.fromObject(coverage);
  return {
    from: today.minus(coverageDuration).startOf('month'),
    to: today.startOf('month'),
    fromExact: today.minus(coverageDuration),
    toExact: today,
  };
};

const updateLastDate = (lastDate, to) => (!lastDate || lastDate.datetime < to.datetime ? to : null);

const getStartGap = (from: ValidSafeDateTime, to: ValidSafeDateTime, requiredRange) => {
  let startGap;
  if (from.datetime <= requiredRange.from && to.datetime >= requiredRange.from) {
    startGap = {};
  } else if (from.datetime > requiredRange.from) {
    startGap = {
      from: requiredRange.fromExact.toObject(),
      to: from.datetime.minus({ months: 1 }).endOf('month').toObject(),
    };
  }
  return startGap;
};

const gapExists = (
  from: ValidSafeDateTime,
  to: ValidSafeDateTime,
  requiredRange,
  rightBoundary
) => (
  rightBoundary.datetime < from.datetime
  && to.datetime <= requiredRange.toExact
  && !withinOneMonth(rightBoundary?.datetime, from?.datetime)
);

export const dateWithinRangeAndOneMonth = (
  date: SafeDateTime,
  rangeFrom: SafeDateTime,
  rangeTo: SafeDateTime
) => (rangeFrom.datetime <= date.datetime
  || withinOneMonth(date.datetime, rangeFrom.datetime))
  && (date.datetime <= rangeTo.datetime
    || withinOneMonth(rangeTo.datetime, date.datetime));

export const flattenDates = (safeDates: SafeDateTime[]) => {
  let flattenedDates: SafeDateTime[] = [];

  // if there are no dates, then there is no flattening necessary
  safeDates.forEach((safeDate: SafeDateTime) => {
    let changed = false;

    if (flattenedDates.length === 0) {
      changed = true;
      flattenedDates.push(safeDate);
    } else {
      const clonedFlattenedDates = cloneDeep(flattenedDates);
      flattenedDates = clonedFlattenedDates.map((flattenedDate: SafeDateTime) => {
        // if safeDate overlaps flattenedDate completely
        if (
          safeDate.from.datetime < flattenedDate.from.datetime
          && flattenedDate.to.datetime < safeDate.to.datetime
        ) {
          changed = true;
          flattenedDate.from = safeDate.from;
          flattenedDate.to = safeDate.to;
        } else {
          // if from date is within the date range, and to date is outside, then update range to date
          if (dateWithinRangeAndOneMonth(safeDate.from, flattenedDate.from, flattenedDate.to)) {
            if (safeDate.to.datetime > flattenedDate.to.datetime) {
              changed = true;
              flattenedDate.to = safeDate.to;
            }
          }
          // if to date is within the date range, and from date is outside, then update range from date
          if (dateWithinRangeAndOneMonth(safeDate.to, flattenedDate.from, flattenedDate.to)) {
            if (safeDate.from.datetime < flattenedDate.from.datetime) {
              changed = true;
              flattenedDate.from = safeDate.from;
            }
          }
        }
        return flattenedDate;
      });
    }
    // if no changes detected for flattening, then add safeDate into flattened date ranges
    if (!changed) {
      flattenedDates.push(safeDate);
    }
  });
  return flattenedDates;
};

export const findTimelineGaps = (
  /** duration object that needs to be covered */
  coverage: DurationObject,
  /** ranges to check against coverage duration */
  ranges?: DeepSafePartial<DateRangeModel[]>
): { from: DateObject; to: DateObject }[] => {
  if (!ranges) { return []; }

  // prepare & sort ranges
  const sortedRanges = sortDateRanges(validateDateRanges(ranges)); // should also sanitize dates to use month only or 1st of month
  const requiredRange = getRequiredRange(coverage);
  const gaps: { from: DateObject; to: DateObject }[] = [];
  let startGap: { from?: DateObject; to?: DateObject } | undefined;
  let lastDate: ValidSafeDateTime;
  let rightBoundary: ValidSafeDateTime;

  const flattenedDates = flattenDates(sortedRanges);

  flattenedDates.forEach(({ from, to }, index) => {
    // update last tracked date; sort only occurs on 'from' date, so possible that last date could be attached to any entry
    const updatedLastDate = updateLastDate(lastDate, to);
    if (updatedLastDate) { lastDate = updatedLastDate; }

    // add initial gap between 'from' and requiredRange 'from' dates
    if (!startGap) {
      startGap = getStartGap(from, to, requiredRange);
      if (startGap?.to) {
        gaps.push(startGap);
      }
      rightBoundary = to;
    }

    // identify and add gaps
    if (gapExists(from, to, requiredRange, rightBoundary)) {
      gaps.push({
        from: rightBoundary.datetime.plus({ months: 1 }).toObject(),
        to: from.datetime.minus({ months: 1 }).endOf('month').toObject(),
      });
      rightBoundary = to;
    } else if (shouldUpdateRightBoundry({
      from, to, requiredRange, rightBoundary
    })) {
      rightBoundary = to;
    }

    // add last date gap if exists
    if (index === flattenedDates.length - 1 && lastDate.datetime < requiredRange.to) {
      gaps.push({
        from: withinOneMonth(lastDate.datetime, requiredRange.toExact)
          ? lastDate.datetime.toObject()
          : lastDate.datetime.plus({ months: 1 }).toObject(),
        to: requiredRange.toExact.toObject(),
      });
    }
  });

  return gaps;
};

export const convertDateRangesToMarkdown = (dateRanges: { from: DateObject; to: DateObject }[]) => {
  const context = dateRanges
    .map((dateRange) => {
      const from = getSafeDateTime(dateRange.from);
      const to = getSafeDateTime(dateRange.to);
      return `* ${from.datetime?.toLocaleString()} - ${to.datetime?.toLocaleString()}`;
    })
    .join('\n');

  return context;
};

export const isLuxonDateInstance = (obj: DateTime | object | undefined) => obj instanceof DateTime;

export const setToStartOfMonth = (date: DateTime | DateModel | SafeDateTime | undefined) => {
  // Would really like to rethink the date structure...currently using getSafeDateTime or date object,
  // but need to move everything to either Luxon or Moment default structure (without 'valid' param);
  // too many additional checks are required because we do not know which structure the date is coming in as
  const result = date;
  if (isLuxonDateInstance(date?.datetime)) {
    try {
      result.datetime = DateTime.fromObject(date.datetime.c).startOf('month');
      return result;
    } catch (e) {
      return result;
    }
  }
  // used for default date object structure; should use Luxon/Moment everywhere instead
  return result?.day ? { ...result, day: '1' } : result;
};

export const isValid = (date): boolean => {
  const safeDate = getSafeDateTime(date);
  if (safeDate.valid) { return true; }
  return false;
};

export const isEndBeforeStart = (end: SafeDateTime, start: SafeDateTime) => end?.datetime && start?.datetime && end.datetime < start.datetime;

export const allDatesValid = (dates = []) => dates?.every(({ from, to }) => isValid(from) && isValid(to));

export const getThresholdYears = (formType) => (formType === SF852013 ? 16 : 18);

export const toDddmmyy = (dateModel: DateModel): string => {
  const { day, month, year } = dateModel;

  const paddedDay = day ? day.padStart(2, '0') : '--';
  const paddedMonth = month ? month.padStart(2, '0') : '--';
  const paddedYear = year ? year.padStart(2, '0') : '--';
  const slicedYear = paddedYear.slice(-2);
  return paddedDay + paddedMonth + slicedYear;
};
/**
 * mmddyyyy is the format used in Verification Process
 * @param dateModel
 * @returns
 */
export const toMmddyyyy = (dateModel: DateModel): string => {
  const { day, month, year } = dateModel;

  const paddedDay = day ? day.padStart(2, '0') : '--';
  const paddedMonth = month ? month.padStart(2, '0') : '--';
  const paddedYear = year ? year.padStart(4, '0') : '--';
  return paddedMonth + paddedDay + paddedYear;
};
